// Die App haelt sich selbst aktuell.
//
// Die APK kommt aus einem GitHub Release, aber nicht direkt von GitHub: den
// Release holt der eigene Server (server/src/updates.js), er kennt den Token
// und reicht auch die Datei durch. Die App sieht nur `/api/update` und
// `/api/update/apk`.
//
// Der Vergleich ist bewusst stumpf eine Zahl: die GitHub Action setzt den
// `versionCode` der APK auf die Nummer des Workflow-Laufs, und dieselbe Nummer
// steht im Tag (`build-<Nummer>`). Was groesser ist, ist neuer.
//
// Im Browser gibt es hier nichts zu tun - dort liefert der Server die
// Oberflaeche aus, ein Neuladen genuegt.

import { App } from '@capacitor/app';
import { Capacitor, registerPlugin, type PluginListenerHandle } from '@capacitor/core';
import { request } from './api';
import { getServerBase } from './server';

export interface ReleaseInfo {
  tag: string;
  name: string;
  /** null, wenn der Tag keiner Versionsnummer entspricht. */
  versionCode: number | null;
  notes: string;
  publishedAt: string | null;
  releaseUrl: string;
  apk: { name: string; size: number; url: string } | null;
}

export interface UpdateInfo {
  /** Was gerade installiert ist. */
  current: number;
  currentName: string;
  release: ReleaseInfo;
  /** Neuer als das Installierte - und es gibt eine Datei dazu. */
  available: boolean;
}

interface AppUpdaterPlugin {
  canInstall(): Promise<{ granted: boolean }>;
  openInstallSettings(): Promise<void>;
  download(options: { url: string; fileName?: string }): Promise<{ path: string; size: number }>;
  install(options: { path: string }): Promise<void>;
  addListener(
    event: 'progress',
    handler: (data: { loaded: number; total: number }) => void,
  ): Promise<PluginListenerHandle>;
}

const AppUpdater = registerPlugin<AppUpdaterPlugin>('AppUpdater');

/** Nur die verpackte Android-App kann sich selbst ersetzen. */
export function updatesSupported(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';
}

/**
 * Ein Android-Geraet im Browser statt in der App.
 *
 * Fuer diesen Fall gibt es keinen Installationsweg ueber den Play Store, und
 * "zum Startbildschirm hinzufuegen" macht aus der Web-App keine App mit
 * Hintergrund-Tracking. Bleibt die APK vom eigenen Server.
 */
export function androidBrowser(): boolean {
  return !Capacitor.isNativePlatform() && /Android/i.test(navigator.userAgent);
}

/** Die APK des neuesten Releases - null, wenn der Server keine anbietet. */
export async function apkDownload(): Promise<ReleaseInfo['apk']> {
  const release = await request<ReleaseInfo>('/api/update');
  return release.apk;
}

/** Die installierte Version - `build` ist der versionCode aus der APK. */
async function installedVersion(): Promise<{ code: number; name: string }> {
  const info = await App.getInfo();
  const code = Number(info.build);
  return { code: Number.isFinite(code) ? code : 0, name: info.version };
}

/**
 * Was gibt es Neues? Wirft, wenn der Server nicht antwortet oder die
 * Update-Pruefung dort abgeschaltet ist (Status 503).
 */
export async function checkForUpdate({ fresh = false } = {}): Promise<UpdateInfo> {
  const release = await request<ReleaseInfo>(`/api/update${fresh ? '?fresh=1' : ''}`);
  const current = await installedVersion();
  return {
    current: current.code,
    currentName: current.name,
    release,
    available:
      release.apk !== null &&
      typeof release.versionCode === 'number' &&
      release.versionCode > current.code,
  };
}

const LAST_CHECK_KEY = 'r2f.update.lastCheck';
const CHECK_INTERVAL_MS = 6 * 60 * 60 * 1000;

/**
 * Beim Start wird nicht bei jedem Oeffnen der App gefragt - hoechstens alle
 * sechs Stunden. Wer sofort wissen will, was es gibt, tippt in den
 * Einstellungen auf "Jetzt pruefen".
 */
export function dueForAutoCheck(): boolean {
  if (!updatesSupported()) return false;
  try {
    const last = Number(localStorage.getItem(LAST_CHECK_KEY));
    return !Number.isFinite(last) || Date.now() - last > CHECK_INTERVAL_MS;
  } catch {
    return true;
  }
}

export function markAutoChecked(): void {
  try {
    localStorage.setItem(LAST_CHECK_KEY, String(Date.now()));
  } catch {
    // Ohne Speicher wird eben bei jedem Start gefragt.
  }
}

/** Darf die App den Installationsdialog oeffnen? */
export async function canInstall(): Promise<boolean> {
  if (!updatesSupported()) return false;
  try {
    return (await AppUpdater.canInstall()).granted;
  } catch {
    return false;
  }
}

/** Oeffnet "Unbekannte Apps installieren" fuer diese App. */
export async function openInstallSettings(): Promise<void> {
  await AppUpdater.openInstallSettings();
}

/**
 * Laedt die APK und uebergibt sie an den Installationsdialog von Android.
 * Bestaetigen muss der Nutzer dort selbst - im Stillen installiert hier
 * nichts.
 */
export async function downloadAndInstall(
  release: ReleaseInfo,
  onProgress?: (fraction: number) => void,
): Promise<void> {
  if (!release.apk) throw new Error('Zu dieser Version gibt es keine Datei.');

  const listener = onProgress
    ? await AppUpdater.addListener('progress', ({ loaded, total }) => {
        const known = total > 0 ? total : release.apk?.size ?? 0;
        onProgress(known > 0 ? Math.min(1, loaded / known) : 0);
      })
    : null;

  try {
    const { path } = await AppUpdater.download({
      url: `${getServerBase()}${release.apk.url}`,
      fileName: release.apk.name,
    });
    await AppUpdater.install({ path });
  } finally {
    await listener?.remove();
  }
}

/** Eine Groesse, wie sie ein Mensch liest. */
export function formatSize(bytes: number): string {
  if (!bytes) return '';
  const mb = bytes / (1024 * 1024);
  return `${mb.toFixed(1).replace('.', ',')} MB`;
}
